import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { hasAnalyticsConsent, setAnalyticsConsent } from "@/lib/analytics";
import { supabase } from "@/integrations/supabase/client";

const POLICY_VERSION = "2026-05-09";

function getOrCreateAnonId(): string {
  try {
    const k = "bp.anon_id";
    const cur = localStorage.getItem(k);
    if (cur) return cur;
    const id = crypto.randomUUID();
    localStorage.setItem(k, id);
    return id;
  } catch {
    return "no-storage";
  }
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ConsentPreferencesDialog({ open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    if (open) setAnalytics(hasAnalyticsConsent());
  }, [open]);

  const persist = async () => {
    setAnalyticsConsent(analytics);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      await supabase.from("consent_log").insert({
        user_id: user?.id ?? null,
        anon_id: user?.id ? null : getOrCreateAnonId(),
        necessary: true,
        analytics,
        marketing,
        policy_version: POLICY_VERSION,
        user_agent: navigator.userAgent.slice(0, 500),
      });
    } catch {
      // best-effort; don't block UX on logging failure
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("consent.dialog.title")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
            <p className="text-sm text-muted-foreground">{t("consent.dialog.essential")}</p>
            <Switch checked disabled aria-label="essential" />
          </div>
          <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
            <p className="text-sm text-muted-foreground">{t("consent.dialog.analytics")}</p>
            <Switch checked={analytics} onCheckedChange={setAnalytics} aria-label="analytics" />
          </div>
          <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
            <p className="text-sm text-muted-foreground">Marketing cookies (personalized offers, retargeting)</p>
            <Switch checked={marketing} onCheckedChange={setMarketing} aria-label="marketing" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={persist}>
            {t("consent.dialog.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
